import { CommonModule } from '@angular/common';
import { Component, computed, effect, OnDestroy, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { AppStateService } from '../../services/app-state.service';
import { Reminder } from '../../models/campaign.model';
import { PeoplePickerComponent } from '../people-picker/people-picker.component';
import { ApproverPickerComponent } from '../approver-picker/approver-picker.component';
import { AudienceDialogComponent } from '../audience-dialog/audience-dialog.component';
import { MultiSelectDropdownComponent } from '../multi-select-dropdown/multi-select-dropdown.component';
import { TimezonePickerComponent, zoneOffsetLabel } from '../timezone-picker/timezone-picker.component';

@Component({
  selector: 'app-campaign-form',
  standalone: true,
  imports: [CommonModule, FormsModule, PeoplePickerComponent, ApproverPickerComponent, AudienceDialogComponent, MultiSelectDropdownComponent, TimezonePickerComponent],
  templateUrl: './campaign-form.component.html',
  styleUrls: ['./campaign-form.component.css'],
})
export class CampaignFormComponent implements OnDestroy {
  readonly priorities = ['Low', 'Medium', 'High'];
  readonly channels = ['Teams', 'Email'];
  readonly reminderCounts = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];

  genPrompt = '';
  generating = false;
  generateError = '';
  generateSuccess = '';

  schedule = false;
  sendDate = '';
  sendTime = '';
  approverEmail = '';

  remindersOn = false;
  reminderRows: { date: string; time: string }[] = [];
  reminderTimezone = '';

  sendError = '';
  sendSuccess = '';
  private sendSuccessTimeout?: ReturnType<typeof setTimeout>;
  resolveError = '';
  resolving = false;
  sending = false;

  /** Emails already covered by the selected teams/locations (no inclusions/exclusions applied). */
  baseAudienceEmails = signal<Set<string>>(new Set());
  private lastAudienceBaseKey = '';

  /** Base audience plus any inclusions already added — what exclusions are checked against. */
  exclusionCheckEmails = computed(() => {
    const base = this.baseAudienceEmails();
    const incl = this.state.form().audience.inclusions.map((e) => e.trim().toLowerCase());
    return new Set([...base, ...incl]);
  });

  constructor(public state: AppStateService, private api: ApiService) {
    this.api.getTeams().subscribe({
      next: (teams) => this.state.teamsOptions.set(teams),
      error: () => this.state.teamsOptions.set([]),
    });
    this.api.getLocations().subscribe({
      next: (locations) => this.state.locationsOptions.set(locations),
      error: () => this.state.locationsOptions.set([]),
    });

    const existing = this.state.form().send_datetime;
    if (existing) {
      const dt = new Date(existing);
      this.schedule = true;
      this.sendDate = dt.toISOString().slice(0, 10);
      this.sendTime = dt.toISOString().slice(11, 16);
    }

    this.syncApproverFromForm();

    this.syncRemindersFromForm();

    // The form can also change from outside this component — a chat reply or
    // loading a saved thread. Re-derive the row editors when that happens, but
    // only when the form genuinely differs from what the rows already hold, so
    // our own writes in syncRemindersToForm() don't retrigger this.
    effect(() => {
      const reminders = this.state.form().reminders ?? [];
      if (JSON.stringify(reminders) !== JSON.stringify(this.rowsToReminders())) {
        this.syncRemindersFromForm();
      }
    });

    // Re-resolve the base audience (teams/locations only) whenever it changes,
    // so the people-pickers can warn about redundant inclusions/exclusions.
    effect(() => {
      const audience = this.state.form().audience;
      const key = JSON.stringify({ teams: audience.teams, locations: audience.locations, copilot_users_only: audience.copilot_users_only });
      if (key !== this.lastAudienceBaseKey) {
        this.lastAudienceBaseKey = key;
        this.refreshBaseAudience(audience.teams, audience.locations, audience.copilot_users_only, key);
      }
    });
  }

  ngOnDestroy(): void {
    if (this.sendSuccessTimeout) {
      clearTimeout(this.sendSuccessTimeout);
    }
  }

  private refreshBaseAudience(teams: string[], locations: string[], copilot_users_only: boolean, key: string): void {
    this.baseAudienceEmails.set(new Set());
    if (teams.length === 0 && locations.length === 0) {
      return;
    }
    this.api.resolveAudience({ teams, locations, copilot_users_only, inclusions: [], exclusions: [] }).subscribe({
      next: (res) => {
        if (key === this.lastAudienceBaseKey) {
          this.baseAudienceEmails.set(new Set(res.people.map((p) => p.email.trim().toLowerCase())));
        }
      },
      error: () => {
        if (key === this.lastAudienceBaseKey) {
          this.baseAudienceEmails.set(new Set());
        }
      },
    });
  }

  private reminderSeedStart(): { date: string; time: string } {
    const sendout = this.state.form().send_datetime;
    const source = sendout ? new Date(sendout) : new Date();
    const nextDay = this.addDays(source, 1);
    return {
      date: this.toDateString(nextDay),
      time: `${this.pad(nextDay.getHours())}:${this.pad(nextDay.getMinutes())}`,
    };
  }

  generate(): void {
    this.generateError = '';
    this.generateSuccess = '';
    if (!this.genPrompt.trim()) {
      this.generateError = 'Enter a description first.';
      return;
    }
    this.generating = true;
    this.api.generateCampaign(this.genPrompt).subscribe({
      next: (draft) => {
        this.state.setForm(draft);
        this.generateSuccess = 'Draft generated — review & edit below.';
        this.generating = false;
        this.syncScheduleFromForm();
        this.syncApproverFromForm();
        this.syncRemindersFromForm();
      },
      error: (err) => {
        this.generateError = `Generate failed: ${err.message || err}`;
        this.generating = false;
      },
    });
  }

  private syncScheduleFromForm(): void {
    const existing = this.state.form().send_datetime;
    if (existing) {
      const dt = new Date(existing);
      this.schedule = true;
      this.sendDate = dt.toISOString().slice(0, 10);
      this.sendTime = dt.toISOString().slice(11, 16);
    } else {
      this.schedule = false;
      this.sendDate = '';
      this.sendTime = '';
    }
  }

  private syncApproverFromForm(): void {
    this.approverEmail = this.state.form().approver || '';
  }

  onScheduleToggle(checked: boolean): void {
    this.schedule = checked;
    this.updateSendDatetime();
  }

  updateSendDatetime(): void {
    if (this.schedule && this.sendDate && this.sendTime) {
      const iso = `${this.sendDate}T${this.sendTime}:00`;
      this.state.form.update((f) => ({ ...f, send_datetime: iso }));
    } else if (!this.schedule) {
      this.state.form.update((f) => ({ ...f, send_datetime: null }));
    }
  }

  updateField<K extends string>(key: K, value: any): void {
    this.state.form.update((f: any) => ({ ...f, [key]: value }));
  }

  onApproverChange(email: string): void {
    this.updateField('approver', email);
  }

  // ── Reminders ──────────────────────────────────────────────────────────────
  onRemindersToggle(checked: boolean): void {
    this.remindersOn = checked;
    if (checked) {
      if (!this.reminderTimezone) {
        this.reminderTimezone = this.detectTimezone();
      }
      if (!this.reminderRows.length) {
        const seed = this.reminderSeedStart();
        this.reminderRows = this.seedReminderRows(1, this.parseDate(seed.date), seed.time);
      }
    } else {
      this.reminderRows = [];
      this.reminderTimezone = '';
    }
    this.syncRemindersToForm();
  }

  onReminderTimezoneChange(zone: string): void {
    // The typed times keep their numbers and change meaning — picking a new
    // zone means "these times, over there", not "the same instant re-rendered".
    this.reminderTimezone = zone;
    this.syncRemindersToForm();
  }

  onReminderCountChange(count: number | string): void {
    const target = Math.max(1, Math.min(Number(count) || 1, this.reminderCounts.length));
    const rows = this.reminderRows;

    if (target < rows.length) {
      // Shrinking: drop from the end, leaving earlier edits untouched.
      this.reminderRows = rows.slice(0, target);
    } else if (target > rows.length) {
      // Growing: continue the sequence from the last row so prior edits stand.
      const last = rows[rows.length - 1];
      const seed = this.reminderSeedStart();
      const anchor = last ? this.addDays(this.parseDate(last.date), 1) : this.parseDate(seed.date);
      const time = last ? last.time : seed.time;
      this.reminderRows = [...rows, ...this.seedReminderRows(target - rows.length, anchor, time)];
    }

    this.syncRemindersToForm();
  }

  updateReminderRow(index: number, key: 'date' | 'time', value: string): void {
    const row = this.reminderRows[index];
    if (!row) {
      return;
    }
    this.reminderRows = this.reminderRows.map((r, i) =>
      i === index ? { ...r, [key]: value } : r,
    );
    this.syncRemindersToForm();
  }

  /** Build `count` rows on consecutive days, all at the same time of day. */
  private seedReminderRows(count: number, start: Date, time: string): { date: string; time: string }[] {
    const rows: { date: string; time: string }[] = [];
    for (let i = 0; i < count; i++) {
      rows.push({ date: this.toDateString(this.addDays(start, i)), time });
    }
    return rows;
  }

  /** Mirror the row editors into the campaign form as a numbered reminder list. */
  private syncRemindersToForm(): void {
    const reminders = this.rowsToReminders();
    const reminder_timezone = reminders.length ? this.reminderTimezone : '';
    this.state.form.update((f) => ({
      ...f,
      reminders,
      reminder_timezone,
      reminder_timezone_offset: reminder_timezone
        ? this.offsetForFirstReminder(reminder_timezone, reminders)
        : '',
    }));
  }

  /**
   * The zone's offset at the first reminder, not at form-fill time.
   *
   * A zone observing daylight saving has two offsets across the year, so this
   * is only exact for reminders sharing the first one's DST period. The IANA
   * name in reminder_timezone stays authoritative; this field is a convenience.
   */
  private offsetForFirstReminder(zone: string, reminders: Reminder[]): string {
    const first = reminders[0];
    const at = first
      ? this.toInstant(first.send_datetime.slice(0, 10), first.send_datetime.slice(11, 16), zone)
      : null;
    return zoneOffsetLabel(zone, at ?? new Date());
  }

  /** Complete rows only — a half-typed date shouldn't reach the campaign JSON. */
  private rowsToReminders(): Reminder[] {
    return this.reminderRows
      .filter((row) => row.date && row.time)
      .map((row, index) => ({
        reminder_number: index + 1,
        send_datetime: `${row.date}T${row.time}:00`,
      }));
  }

  /** Rebuild the row editors from the form (generate, or loading a saved thread). */
  private syncRemindersFromForm(): void {
    const form = this.state.form();
    const reminders = form.reminders ?? [];
    this.reminderRows = reminders
      .slice()
      .sort((a, b) => a.reminder_number - b.reminder_number)
      .map((reminder) => ({
        date: reminder.send_datetime.slice(0, 10),
        time: reminder.send_datetime.slice(11, 16),
      }));
    this.remindersOn = this.reminderRows.length > 0;
    this.reminderTimezone = this.remindersOn
      ? form.reminder_timezone || this.detectTimezone()
      : '';
  }

  /** The requestor's own zone, from the OS via the browser. */
  private detectTimezone(): string {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  }

  /** Resolve a local wall-clock string in `zone` to a 'YYYY-MM-DDTHH:mm:ss' UTC string. */
  private toUtcString(local: string | null | undefined, zone: string): string | null {
    if (!local) {
      return null;
    }
    const instant = this.toInstant(local.slice(0, 10), local.slice(11, 16), zone);
    return instant ? instant.toISOString().slice(0, 19) : null;
  }

  /**
   * What a row's wall-clock time actually means elsewhere.
   *
   * Shown so a requestor scheduling in another zone can see they haven't picked
   * the middle of someone's night. Returns '' when there's nothing useful to add.
   */
  reminderPreview(row: { date: string; time: string }): string {
    if (!row.date || !row.time || !this.reminderTimezone) {
      return '';
    }
    const instant = this.toInstant(row.date, row.time, this.reminderTimezone);
    if (!instant) {
      return '';
    }

    const local = this.detectTimezone();
    const targets = local === this.reminderTimezone ? ['UTC'] : [local, 'UTC'];
    return targets
      .map((zone) => `${this.formatIn(instant, zone)} ${this.shortZone(zone, local)}`)
      .join(' · ');
  }

  /**
   * Resolve a wall-clock time in `zone` to the absolute instant it denotes.
   *
   * Date arithmetic can't do this directly, so we guess the instant, ask what
   * wall-clock time that guess lands on in `zone`, and correct by the gap. The
   * second pass handles DST transitions, where the first correction can
   * overshoot into a different offset.
   */
  private toInstant(date: string, time: string, zone: string): Date | null {
    const guess = new Date(`${date}T${time}:00Z`);
    if (Number.isNaN(guess.getTime())) {
      return null;
    }
    let instant = guess;
    for (let pass = 0; pass < 2; pass++) {
      const drift = this.wallClockUtc(instant, zone) - instant.getTime();
      if (!drift) {
        break;
      }
      instant = new Date(instant.getTime() - drift);
    }
    return instant;
  }

  /** The wall-clock reading of `instant` in `zone`, as a UTC-based timestamp. */
  private wallClockUtc(instant: Date, zone: string): number {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).formatToParts(instant);

    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
    // 'en-US' renders midnight as hour 24; Date.UTC normalises it either way.
    return Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour') % 24,
      get('minute'),
      get('second'),
    );
  }

  private formatIn(instant: Date, zone: string): string {
    try {
      return new Intl.DateTimeFormat('en-GB', {
        timeZone: zone,
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).format(instant);
    } catch {
      return '';
    }
  }

  private shortZone(zone: string, local: string): string {
    if (zone === 'UTC') {
      return 'UTC';
    }
    const city = zone.split('/').pop()?.replace(/_/g, ' ') ?? zone;
    return zone === local ? `${city} (your time)` : city;
  }

  // Local-time date helpers. Deliberately avoids toISOString(), which shifts to
  // UTC and can land on the previous/next day for non-UTC users.
  private startOfToday(): Date {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  }

  private currentTime(): string {
    const now = new Date();
    return `${this.pad(now.getHours())}:${this.pad(now.getMinutes())}`;
  }

  private addDays(date: Date, days: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
  }

  private parseDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
      ? new Date(year, month - 1, day)
      : this.startOfToday();
  }

  private toDateString(date: Date): string {
    return `${date.getFullYear()}-${this.pad(date.getMonth() + 1)}-${this.pad(date.getDate())}`;
  }

  private pad(value: number): string {
    return String(value).padStart(2, '0');
  }
  updateAudienceField(key: 'locations' | 'teams', value: string[]): void;
  updateAudienceField(key: 'copilot_users_only', value: boolean): void;
  updateAudienceField(key: 'locations' | 'teams' | 'copilot_users_only', value: string[] | boolean): void {
    this.state.form.update((f) => ({ ...f, audience: { ...f.audience, [key]: value } }));
    this.state.peekResults.set(null);
  }

  peekAudience(): void {
    this.resolveError = '';
    this.resolving = true;
    const audience = this.state.form().audience;
    const key = JSON.stringify(audience);
    this.api.resolveAudience(audience).subscribe({
      next: (res) => {
        if (key === JSON.stringify(this.state.form().audience)) {
          this.state.peekResults.set(res);
        }
        this.resolving = false;
      },
      error: (err) => {
        this.resolveError = `Resolve failed: ${err.message || err}`;
        this.state.peekResults.set(null);
        this.resolving = false;
      },
    });
  }

  closePeek(): void {
    this.state.peekResults.set(null);
  }

  sendCampaign(): void {
    if (this.sendSuccessTimeout) {
      clearTimeout(this.sendSuccessTimeout);
      this.sendSuccessTimeout = undefined;
    }
    this.sendError = '';
    this.sendSuccess = '';
    this.sending = true;
    // Compute each reminder's exact UTC from the single campaign zone (DST-aware)
    // so scheduling stays correct even across a daylight-saving change.
    const form = this.state.form();
    const zone = form.reminder_timezone || this.reminderTimezone || this.detectTimezone();
    const reminders = (form.reminders ?? []).map((r) => ({
      ...r,
      send_datetime_utc: this.toUtcString(r.send_datetime, zone),
    }));
    const outgoing = {
      ...form,
      reminder_timezone: zone,
      reminder_timezone_offset: this.offsetForFirstReminder(zone, form.reminders ?? []),
      send_datetime_utc: this.toUtcString(form.send_datetime, zone),
      reminders,
    };
    this.api.sendCampaign(
      outgoing,
      this.state.activeThreadId(),
      this.state.activeMessageId(),
    ).subscribe({
      next: (resp) => {
        const form = this.state.form();
        const summary = this.state.buildApprovalSummary(
          form,
          resp.audience_count || 0,
          resp.campaign_alias || resp.campaign_id,
          resp.version_number,
        );
        this.state.addMessage({ role: 'user', content: summary });
        this.state.addMessage({ role: 'assistant', content: resp.detail || 'Submitted.' });
        this.state.setConversationContext(resp.thread_id, resp.message_id, resp.conversation_id);
        this.state.refreshConversationThreads();
        this.sendSuccess = `Campaign submitted for approval. Campaign ID: ${resp.campaign_alias || resp.campaign_id}. Version: ${resp.version_number}`;
        this.sendSuccessTimeout = setTimeout(() => {
          this.sendSuccess = '';
          this.sendSuccessTimeout = undefined;
        }, 20000);
        this.sending = false;
      },
      error: (err) => {
        const detail = err?.error?.detail;
        this.sendError = `Send failed: ${detail || err.message || err}`;
        this.sending = false;
      },
    });
  }
}
